import type { LlmRouteDefinitionBase, LlmRouteGroup } from './types';

export const ROUTE_POLICY_VERSION = 'consumer-llm-routing-2026-05-22.v1';

export const MODEL_ROUTE_IDS = {
  ikigai: {
    sourceSummary: 'ikigai.sourceSummary',
    entitySynthesis: 'ikigai.entitySynthesis',
    selfAudit: 'ikigai.selfAudit',
    entityComposition: 'ikigai.entityComposition',
    profileSummary: 'ikigai.profileSummary',
  },
  missions: {
    intentExtraction: 'missions.intentExtraction',
    searchKeywords: 'missions.searchKeywords',
    agentExecution: 'missions.agentExecution',
    refinement: 'missions.refinement',
    predictionText: 'missions.predictionText',
    diagnosticInsights: 'missions.diagnosticInsights',
  },
  iab: {
    inputPreprocessing: 'iab.inputPreprocessing',
    demographics: 'iab.demographics',
    household: 'iab.household',
    interests: 'iab.interests',
    purchase: 'iab.purchase',
    evidenceJudge: 'iab.evidenceJudge',
  },
} as const;

export const IAB_EVIDENCE_JUDGE_ROUTE_ID = MODEL_ROUTE_IDS.iab.evidenceJudge;

const textValidation = { kind: 'nonEmptyText', minChars: 20 } as const;
const shortTextValidation = { kind: 'nonEmptyText', minChars: 3 } as const;

const fastTextDefaults = {
  strategy: 'fastLowCost',
  catalogFilter: 'textGeneration',
  preferLocal: true,
  allowCloud: true,
} as const;

const balancedTextDefaults = {
  strategy: 'balancedQuality',
  catalogFilter: 'textGeneration',
  preferLocal: true,
  allowCloud: true,
} as const;

const longContextDefaults = {
  strategy: 'longContextSummary',
  catalogFilter: 'longContextTextGeneration',
  preferLocal: true,
  allowCloud: true,
} as const;

const structuredDefaults = {
  strategy: 'structuredReliability',
  catalogFilter: 'jsonCapableTextGeneration',
  preferLocal: true,
  allowCloud: true,
} as const;

const lowCostSummarizationFit = {
  taskType: 'summarization',
  privacyPosture: 'localPreferredCloudAllowed',
  hardGates: ['textGeneration'],
  softSignals: ['lowLatency', 'lowCost', 'summarizationQuality', 'longContext'],
} as const;

const structuredExtractionFit = {
  taskType: 'structuredExtraction',
  privacyPosture: 'localPreferredCloudAllowed',
  hardGates: ['textGeneration', 'jsonOutput'],
  softSignals: ['lowLatency', 'lowCost', 'structuredReliability'],
} as const;

const classificationFit = {
  taskType: 'classification',
  privacyPosture: 'localPreferredCloudAllowed',
  hardGates: ['textGeneration', 'jsonOutput'],
  softSignals: ['lowLatency', 'lowCost', 'classificationEvidence', 'structuredReliability'],
} as const;

const missionFit = {
  taskType: 'missionGeneration',
  privacyPosture: 'cloudAllowed',
  hardGates: ['textGeneration'],
  softSignals: ['lowLatency', 'lowCost', 'strongReasoning'],
} as const;

const narrativeFit = {
  taskType: 'profileNarrative',
  privacyPosture: 'cloudAllowed',
  hardGates: ['textGeneration'],
  softSignals: ['strongReasoning', 'summarizationQuality'],
} as const;

const standardGuardrails = {
  primaryMetric: 'validationPassRate',
  minValidationPassRate: 0.95,
  maxFallbackRate: 0.05,
} as const;

const iabGuardrails = {
  primaryMetric: 'iabBalancedQuality',
  minValidationPassRate: 0.95,
  minCoverageRate: 0.8,
  minEvidenceQuality: 0.6,
  maxFallbackRate: 0.05,
} as const;

export const MODEL_ROUTE_DEFINITIONS = [
  {
    id: MODEL_ROUTE_IDS.ikigai.sourceSummary,
    group: 'ikigai',
    label: 'Source summaries',
    description: 'Per-source summaries before cross-source analysis',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'ikigai.sourceSummary.prompt.v1',
    outputSchemaVersion: 'ikigai.sourceSummary.text.v1',
    validationPolicy: textValidation,
    defaultSelector: longContextDefaults,
    modelFitPolicy: lowCostSummarizationFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.ikigai.entitySynthesis,
    group: 'ikigai',
    label: 'Entity and opportunity synthesis',
    description: 'Cross-source love entity and mission opportunity generation',
    legacyTier: 'standard',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'ikigai.entitySynthesis.prompt.v1',
    outputSchemaVersion: 'ikigai.entitySynthesis.json.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'ikigai.entitySynthesis.schema.v1' },
    defaultSelector: structuredDefaults,
    modelFitPolicy: structuredExtractionFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.ikigai.selfAudit,
    group: 'ikigai',
    label: 'Self-audit',
    description: 'Post-analysis consistency and hallucination checks',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'ikigai.selfAudit.prompt.v1',
    outputSchemaVersion: 'ikigai.selfAudit.json.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'ikigai.selfAudit.schema.v1' },
    defaultSelector: structuredDefaults,
    modelFitPolicy: structuredExtractionFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.ikigai.entityComposition,
    group: 'ikigai',
    label: 'Entity composition',
    description: 'Merging duplicate entities into cleaner memories',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'ikigai.entityComposition.prompt.v1',
    outputSchemaVersion: 'ikigai.entityComposition.json.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'ikigai.entityComposition.schema.v1' },
    defaultSelector: structuredDefaults,
    modelFitPolicy: structuredExtractionFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.ikigai.profileSummary,
    group: 'ikigai',
    label: 'Character profile summary',
    description: 'User-facing narrative summary generation',
    legacyTier: 'standard',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'ikigai.profileSummary.prompt.v1',
    outputSchemaVersion: 'ikigai.profileSummary.json.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'ikigai.profileSummary.schema.v1' },
    defaultSelector: balancedTextDefaults,
    modelFitPolicy: narrativeFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.missions.intentExtraction,
    group: 'missions',
    label: 'Intent extraction',
    description: 'Turns user requests into structured mission intents',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'missions.intentExtraction.prompt.v1',
    outputSchemaVersion: 'missions.intentExtraction.json.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'missions.intentExtraction.schema.v1' },
    defaultSelector: structuredDefaults,
    modelFitPolicy: structuredExtractionFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.missions.searchKeywords,
    group: 'missions',
    label: 'Search keyword optimization',
    description: 'Turns entity context into concise API search queries',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'missions.searchKeywords.prompt.v1',
    outputSchemaVersion: 'missions.searchKeywords.text.v1',
    validationPolicy: shortTextValidation,
    defaultSelector: fastTextDefaults,
    modelFitPolicy: missionFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.missions.agentExecution,
    group: 'missions',
    label: 'Agent execution helpers',
    description: 'LLM helpers inside mission agents such as purchase intent and recipes',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'missions.agentExecution.prompt.v1',
    outputSchemaVersion: 'missions.agentExecution.text.v1',
    validationPolicy: shortTextValidation,
    defaultSelector: fastTextDefaults,
    modelFitPolicy: missionFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.missions.refinement,
    group: 'missions',
    label: 'Mission refinement',
    description: 'Conversational updates to existing mission cards',
    legacyTier: 'standard',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'missions.refinement.prompt.v1',
    outputSchemaVersion: 'missions.refinement.json.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'missions.refinement.schema.v1' },
    defaultSelector: balancedTextDefaults,
    modelFitPolicy: missionFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.missions.predictionText,
    group: 'missions',
    label: 'Prediction text',
    description: 'Short personalized explanation on mission cards',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'missions.predictionText.prompt.v1',
    outputSchemaVersion: 'missions.predictionText.text.v1',
    validationPolicy: shortTextValidation,
    defaultSelector: fastTextDefaults,
    modelFitPolicy: missionFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.missions.diagnosticInsights,
    group: 'missions',
    label: 'Diagnostic insights',
    description: 'Profile completeness and data-quality insight generation',
    legacyTier: 'standard',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'missions.diagnosticInsights.prompt.v1',
    outputSchemaVersion: 'missions.diagnosticInsights.json.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'missions.diagnosticInsights.schema.v1' },
    defaultSelector: balancedTextDefaults,
    modelFitPolicy: missionFit,
    experimentGuardrailDefaults: standardGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.iab.inputPreprocessing,
    group: 'iab',
    label: 'Input preprocessing',
    description: 'Stage 2 email summarization before IAB classification',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'iab.inputPreprocessing.prompt.v1',
    outputSchemaVersion: 'iab.inputPreprocessing.summaryText.v1',
    validationPolicy: textValidation,
    defaultSelector: longContextDefaults,
    modelFitPolicy: lowCostSummarizationFit,
    experimentGuardrailDefaults: iabGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.iab.demographics,
    group: 'iab',
    label: 'Demographics analyzer',
    description: 'IAB demographics classification',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'iab.demographics.prompt.v1',
    outputSchemaVersion: 'iab.demographics.schema.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'iab.demographics.schema.v1' },
    defaultSelector: structuredDefaults,
    modelFitPolicy: classificationFit,
    experimentGuardrailDefaults: iabGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.iab.household,
    group: 'iab',
    label: 'Household analyzer',
    description: 'IAB household classification',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'iab.household.prompt.v1',
    outputSchemaVersion: 'iab.household.schema.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'iab.household.schema.v1' },
    defaultSelector: structuredDefaults,
    modelFitPolicy: classificationFit,
    experimentGuardrailDefaults: iabGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.iab.interests,
    group: 'iab',
    label: 'Interests analyzer',
    description: 'IAB interests classification',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'iab.interests.prompt.v1',
    outputSchemaVersion: 'iab.interests.schema.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'iab.interests.schema.v1' },
    defaultSelector: structuredDefaults,
    modelFitPolicy: classificationFit,
    experimentGuardrailDefaults: iabGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.iab.purchase,
    group: 'iab',
    label: 'Purchase intent analyzer',
    description: 'IAB purchase-intent classification',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'iab.purchase.prompt.v1',
    outputSchemaVersion: 'iab.purchase.schema.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'iab.purchase.schema.v1' },
    defaultSelector: structuredDefaults,
    modelFitPolicy: classificationFit,
    experimentGuardrailDefaults: iabGuardrails,
  },
  {
    id: MODEL_ROUTE_IDS.iab.evidenceJudge,
    group: 'iab',
    label: 'Evidence judge',
    description: 'Validation pass for IAB classification evidence quality',
    legacyTier: 'fast',
    routePolicyVersion: ROUTE_POLICY_VERSION,
    promptVersion: 'iab.evidenceJudge.prompt.v1',
    outputSchemaVersion: 'iab.evidenceJudge.schema.v1',
    validationPolicy: { kind: 'jsonSchema', schemaId: 'iab.evidenceJudge.schema.v1' },
    defaultSelector: structuredDefaults,
    modelFitPolicy: {
      taskType: 'evidenceJudging',
      privacyPosture: 'localPreferredCloudAllowed',
      hardGates: ['textGeneration', 'jsonOutput'],
      softSignals: ['classificationEvidence', 'structuredReliability', 'strongReasoning'],
    },
    experimentGuardrailDefaults: iabGuardrails,
  },
] as const satisfies readonly LlmRouteDefinitionBase[];

export type ModelRouteId = typeof MODEL_ROUTE_DEFINITIONS[number]['id'];
export type ModelRouteDefinition = typeof MODEL_ROUTE_DEFINITIONS[number];

export const MODEL_ROUTE_GROUPS = [
  { id: 'ikigai', label: 'Ikigai', description: 'Entity detection, opportunity generation, and profile summaries' },
  { id: 'missions', label: 'Missions', description: 'Mission creation, execution helpers, refinement, and card text' },
  { id: 'iab', label: 'IAB classification', description: 'Audience taxonomy preprocessing, analyzers, and evidence validation' },
] as const satisfies readonly LlmRouteGroup[];

export const MODEL_ROUTE_ID_LIST = MODEL_ROUTE_DEFINITIONS.map((route) => route.id) as readonly ModelRouteId[];

export const MODEL_ROUTE_ID_SET: ReadonlySet<ModelRouteId> = new Set(MODEL_ROUTE_ID_LIST);

export const IAB_INPUT_PREPROCESSING_ROUTE_ID = MODEL_ROUTE_IDS.iab.inputPreprocessing;

export const IAB_CLASSIFICATION_ROUTE_IDS = [
  MODEL_ROUTE_IDS.iab.demographics,
  MODEL_ROUTE_IDS.iab.household,
  MODEL_ROUTE_IDS.iab.interests,
  MODEL_ROUTE_IDS.iab.purchase,
  IAB_EVIDENCE_JUDGE_ROUTE_ID,
] as const satisfies readonly ModelRouteId[];

export const IKIGAI_MODEL_ROUTE_IDS = [
  MODEL_ROUTE_IDS.ikigai.sourceSummary,
  MODEL_ROUTE_IDS.ikigai.entitySynthesis,
  MODEL_ROUTE_IDS.ikigai.selfAudit,
  MODEL_ROUTE_IDS.ikigai.entityComposition,
  MODEL_ROUTE_IDS.ikigai.profileSummary,
] as const satisfies readonly ModelRouteId[];

export const MISSION_MODEL_ROUTE_IDS = [
  MODEL_ROUTE_IDS.missions.intentExtraction,
  MODEL_ROUTE_IDS.missions.searchKeywords,
  MODEL_ROUTE_IDS.missions.agentExecution,
  MODEL_ROUTE_IDS.missions.refinement,
  MODEL_ROUTE_IDS.missions.predictionText,
  MODEL_ROUTE_IDS.missions.diagnosticInsights,
] as const satisfies readonly ModelRouteId[];

export const MISSION_WORKER_MODEL_ROUTE_IDS = [
  ...MISSION_MODEL_ROUTE_IDS,
  MODEL_ROUTE_IDS.ikigai.profileSummary,
] as const satisfies readonly ModelRouteId[];

export const MISSION_WORKER_DEFAULT_ROUTE_PRIORITY = [
  MODEL_ROUTE_IDS.missions.agentExecution,
  MODEL_ROUTE_IDS.missions.searchKeywords,
  MODEL_ROUTE_IDS.missions.intentExtraction,
  MODEL_ROUTE_IDS.missions.refinement,
  MODEL_ROUTE_IDS.missions.predictionText,
  MODEL_ROUTE_IDS.missions.diagnosticInsights,
  MODEL_ROUTE_IDS.ikigai.profileSummary,
] as const satisfies readonly ModelRouteId[];

export function isModelRouteId(value: string): value is ModelRouteId {
  return MODEL_ROUTE_ID_SET.has(value as ModelRouteId);
}

export function assertModelRouteId(value: string): ModelRouteId {
  if (!isModelRouteId(value)) {
    throw new Error(`Unknown LLM route id: ${value}`);
  }
  return value;
}

export function getModelRouteDefinition(routeId: ModelRouteId): ModelRouteDefinition;
export function getModelRouteDefinition(routeId: string): ModelRouteDefinition | null;
export function getModelRouteDefinition(routeId: string): ModelRouteDefinition | null {
  return MODEL_ROUTE_DEFINITIONS.find((route) => route.id === routeId) ?? null;
}

export function getModelRoutesByGroup(groupId: ModelRouteDefinition['group']): readonly ModelRouteDefinition[] {
  return MODEL_ROUTE_DEFINITIONS.filter((route) => route.group === groupId);
}
